import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import type { HistoryListResponse, HistoryVersion } from '@/types'

export const historyQueryKeys = {
  list: (entityType: string, entityId: string) =>
    ['history', entityType, entityId] as const,
  version: (entityType: string, entityId: string, timestamp: number) =>
    ['history', entityType, entityId, timestamp] as const,
}

/**
 * Hook to fetch history entries for an entity.
 */
export function useHistory(entityType: string, entityId: string) {
  return useQuery({
    queryKey: historyQueryKeys.list(entityType, entityId),
    queryFn: () =>
      api.get<HistoryListResponse>(`/${entityType}/${entityId}/history`),
    enabled: !!entityType && !!entityId,
  })
}

/**
 * Hook to fetch a specific history version with snapshot.
 */
export function useHistoryVersion(
  entityType: string,
  entityId: string,
  timestamp: number
) {
  return useQuery({
    queryKey: historyQueryKeys.version(entityType, entityId, timestamp),
    queryFn: () =>
      api.get<HistoryVersion>(
        `/${entityType}/${entityId}/history/${timestamp}`
      ),
    enabled: !!entityType && !!entityId && !!timestamp,
  })
}

interface RevertParams {
  version: number
  reason?: string
}

/**
 * Hook to revert an entity to a previous version.
 */
export function useRevertEntity(entityType: string, entityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: RevertParams) =>
      api.post(`/${entityType}/${entityId}/revert`, params),
    onSuccess: () => {
      // Invalidate both the entity and its history
      queryClient.invalidateQueries({
        queryKey: historyQueryKeys.list(entityType, entityId),
      })
      // Invalidate the entity detail cache (e.g. ['reports', entityId])
      queryClient.invalidateQueries({
        queryKey: [entityType, entityId],
      })
    },
  })
}
